// @ts-nocheck
import { Modal, Box, IconButton } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { cloudflareCDN } from "ui";
import { ModalContainer } from "./styles";
import React, { createContext, useContext, useMemo, useRef, useEffect, useState } from "react";

const AuthModalContext = createContext({ setCloseHidden: (_hidden) => {} });

export const useAuthModal = () => useContext(AuthModalContext);

const AnimatedHeightBox = ({ children }) => {
  const contentRef = useRef(null);
  const [height, setHeight] = useState("auto");

  useEffect(() => {
    if (contentRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        if (contentRef.current) {
          const newHeight = contentRef.current.scrollHeight;
          setHeight(`${newHeight}px`);
        }
      });
      
      resizeObserver.observe(contentRef.current);
      
      // Set initial height
      const initialHeight = contentRef.current.scrollHeight;
      setHeight(`${initialHeight}px`);
      
      return () => resizeObserver.disconnect();
    }
  }, [children]);

  return (
    <Box
      sx={{
        height,
        overflow: "visible",
        transition: "height 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        willChange: "height"
      }}
    >
      <Box ref={contentRef} sx={{ overflow: "visible" }}>
        {children}
      </Box>
    </Box>
  );
};

const StyledModal = ({ children, graphic = "none", onClose, ...rest }) => {
  const [closeHidden, setCloseHidden] = useState(false);

  // "signin" / "signup" → auth flow: inject the side graphic, the
  // centralized close button, and vertical centering of content.
  // "none" → AuthModal is a plain modal container; the consumer is
  // expected to render its own chrome. This keeps non-auth consumers
  // (ImageCreator, PaintCreator,
  // UserSubscriptionModal) from getting duplicate close buttons.
  const isAuthChrome = graphic === "signin" || graphic === "signup";
  const graphicSrc =
    graphic === "signin"
      ? "/images/auth-modal-signin-graphic.png"
      : "/images/auth-modal-signup-graphic.png";

  const handleModalClose = (event, reason) => {
    if (closeHidden && (reason === "backdropClick" || reason === "escapeKeyDown")) return;
    onClose?.(event, reason);
  };

  const ctxValue = useMemo(() => ({ setCloseHidden }), []);

  return (
    <AuthModalContext.Provider value={ctxValue}>
      <Modal onClose={handleModalClose} disableEscapeKeyDown={closeHidden} {...rest}>
        <ModalContainer
          sx={!isAuthChrome ? {
            maxWidth: "500px",
            // ModalContainer's 640px minHeight is sized for the
            // auth-flow side graphic. Non-auth consumers (ImageCreator,
            // PaintCreator, ProfileModal,
            // verification screens) often render short content and
            // should size to that content instead of being forced to
            // 640px with a band of empty space below.
            minHeight: "auto",
            // SigninContainer caps .form-root at 380px to fit alongside
            // the auth-flow side graphic. In non-auth-chrome mode
            // (e.g. ProfileModal) there is no graphic, so let the form
            // fill the modal width instead of leaving dead space.
            "& .form-root": { maxWidth: "none", marginLeft: "auto", marginRight: "auto" },
          } : undefined}
        >
          <Box className="modal-content">
            {isAuthChrome && (
              <img
                src={cloudflareCDN(window.location.origin + graphicSrc, "width=1000,fit=scale-down,format=auto")}
                alt=""
                className="auth-graphic"
                aria-hidden="true"
              />
            )}

            <Box className="inner-root">
              {isAuthChrome ? (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      minHeight: "32px",
                      flexShrink: 0,
                    }}
                  >
                    {!closeHidden && (
                      <IconButton
                        aria-label="Close"
                        onClick={onClose}
                        sx={{
                          backgroundColor: "transparent",
                          color: "#8C8C8C",
                          padding: "4px",
                          "&:hover": {
                            backgroundColor: "transparent",
                            color: "#5C5C5C",
                          },
                        }}
                      >
                        <CloseIcon sx={{ width: "24px", height: "24px" }} />
                      </IconButton>
                    )}
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "safe center",
                      minHeight: 0,
                    }}
                  >
                    <AnimatedHeightBox>
                      {children}
                    </AnimatedHeightBox>
                  </Box>
                </>
              ) : (
                <AnimatedHeightBox>
                  {children}
                </AnimatedHeightBox>
              )}
            </Box>
          </Box>
        </ModalContainer>
      </Modal>
    </AuthModalContext.Provider>
  );
};

export default StyledModal;
